import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    Landmark,
    TrendingUp,
    Layers,
    Building2,
    Building,
    FileSearch,
    GitBranch,
    Handshake,
    Globe2,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import { INSTRUMENTS } from "@/lib/siteContent";
import { useI18n } from "@/lib/i18n";
import { translations } from "@/lib/translations";

const MARBLE =
    "https://images.pexels.com/photos/22940100/pexels-photo-22940100.jpeg?auto=compress&cs=tinysrgb&w=2400";

const ICONS = [TrendingUp, Layers, Landmark];

const OPERATIONS = [
    { key: "acquisition", n: "I", icon: Building2 },
    { key: "realestate", n: "II", icon: Building },
    { key: "duediligence", n: "III", icon: FileSearch },
    { key: "reorganisation", n: "IV", icon: GitBranch },
    { key: "partnerships", n: "V", icon: Handshake },
    { key: "expansion", n: "VI", icon: Globe2 },
];

const HORIZONTAL_EXPERTISES = [
    { n: "I", title: "Stratégie & vision long terme" },
    { n: "II", title: "Architecture financière & patrimoniale" },
    { n: "III", title: "Ingénierie juridique" },
    { n: "IV", title: "Fiscalité patrimoniale & corporate" },
    { n: "V", title: "Expertise comptable & audit" },
    { n: "VI", title: "Due diligence & analyse" },
    { n: "VII", title: "Coordination d'experts indépendants" },
    { n: "VIII", title: "Structuration internationale" },
    { n: "IX", title: "Gouvernance & conformité" },
    { n: "X", title: "Advisory dirigeants & family offices" },
];

export default function Expertises() {
    const { t, lang } = useI18n();
    const opCards = translations[lang]?.operations?.cards || translations.fr.operations.cards;
    return (
        <div data-testid="expertises-page" className="bg-xv-ivory">
            {/* HERO */}
            <section className="relative pt-32 md:pt-44 pb-20 md:pb-28 overflow-hidden">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-10 text-xv-gold">
                            <span className="divider-gold" />
                            <span className="overline">
                                Métiers · Strategic advisory
                            </span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1
                            className="font-serif text-xv-black text-[44px] sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.98] tracking-tight max-w-5xl"
                            style={{ fontWeight: 300 }}
                        >
                            Trois métiers,{" "}
                            <em className="not-italic text-xv-midnight">
                                une architecture sur-mesure.
                            </em>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-12 max-w-3xl text-xv-text-secondary text-[17px] md:text-[19px] leading-[1.8] font-light">
                            XV Capital Partners intervient à la croisée
                            du développement d'entreprise, de la
                            structuration patrimoniale et des réseaux
                            internationaux d'experts — chaque mandat
                            étant calibré au plus juste du contexte du
                            dirigeant ou de l'acteur institutionnel
                            accompagné.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* THREE INSTRUMENTS — large editorial cards */}
            <section
                data-testid="instruments"
                className="bg-xv-ivory pb-28 md:pb-40"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="space-y-px bg-xv-line border-t border-b border-xv-line">
                        {INSTRUMENTS.map((ins, i) => {
                            const Icon = ICONS[i] || TrendingUp;
                            return (
                                <Reveal key={ins.title} delay={i * 0.08}>
                                    <div
                                        data-testid={`instrument-${i}`}
                                        className="group bg-xv-ivory hover:bg-xv-cream transition-colors duration-700"
                                    >
                                        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 p-10 md:p-14">
                                            <div className="lg:col-span-4">
                                                <div className="overline text-xv-gold mb-8">
                                                    {ins.kicker}
                                                </div>
                                                <Icon
                                                    strokeWidth={1}
                                                    size={36}
                                                    className="text-xv-midnight mb-8"
                                                />
                                                <h2
                                                    className="font-serif text-[34px] md:text-[42px] lg:text-[48px] text-xv-black leading-[1.05] mb-4"
                                                    style={{ fontWeight: 400 }}
                                                >
                                                    {ins.title}
                                                </h2>
                                                <p className="text-xv-midnight text-[13px] tracking-[0.18em] uppercase mb-8">
                                                    {ins.subtitle}
                                                </p>
                                                <div className="inline-flex items-baseline gap-3 border-t border-xv-gold/40 pt-4">
                                                    <span className="overline text-xv-gold">
                                                        {t("expertises.labelIntervention", "Intervention")}
                                                    </span>
                                                    <span className="font-serif text-xv-black text-[18px] md:text-[20px]">
                                                        {ins.ticket}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="lg:col-span-7 lg:col-start-6">
                                                <p className="text-xv-text-secondary text-[16px] md:text-[18px] leading-[1.9] font-light mb-10">
                                                    {ins.desc}
                                                </p>
                                                <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
                                                    {ins.points.map(
                                                        (p, j) => (
                                                            <div
                                                                key={p}
                                                                className="flex items-start gap-4 border-t border-xv-line pt-4"
                                                            >
                                                                <span className="text-xv-gold text-[10px] tracking-[0.28em] mt-1">
                                                                    {String(
                                                                        j + 1
                                                                    ).padStart(
                                                                        2,
                                                                        "0"
                                                                    )}
                                                                </span>
                                                                <span className="font-serif text-[15px] md:text-[17px] text-xv-black leading-snug">
                                                                    {p}
                                                                </span>
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* HORIZONTAL EXPERTISES BAND */}
            <section
                data-testid="expertises-band"
                className="bg-xv-cream py-28 md:py-36"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid lg:grid-cols-12 gap-12 mb-16">
                        <div className="lg:col-span-5">
                            <Reveal>
                                <div className="flex items-center gap-4 mb-8 text-xv-gold">
                                    <span className="divider-gold" />
                                    <span className="overline">
                                        Expertises mobilisées
                                    </span>
                                </div>
                                <h2
                                    className="font-serif text-xv-black text-[36px] md:text-5xl lg:text-[56px] leading-[1.04]"
                                    style={{ fontWeight: 300 }}
                                >
                                    Dix expertises,{" "}
                                    <em className="not-italic text-xv-midnight">
                                        un seul interlocuteur.
                                    </em>
                                </h2>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 self-end">
                            <Reveal delay={0.1}>
                                <p className="text-xv-text-secondary text-[16px] leading-[1.85] font-light">
                                    Chaque opération mobilise un
                                    sous-ensemble de ces expertises,
                                    coordonné en interne et adossé à un
                                    cercle d'avocats fiscalistes, de
                                    spécialistes du droit des affaires,
                                    d'experts-comptables et de
                                    professionnels du back-office.
                                </p>
                            </Reveal>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-xv-line border border-xv-line">
                        {HORIZONTAL_EXPERTISES.map((e, i) => (
                            <Reveal key={e.title} delay={(i % 5) * 0.05}>
                                <div
                                    data-testid={`expertise-${i}`}
                                    className="group bg-xv-cream p-8 h-full transition-colors duration-700 hover:bg-xv-ivory"
                                >
                                    <div className="flex items-start justify-between mb-6">
                                        <span
                                            className="font-serif text-2xl text-xv-gold"
                                            style={{ fontWeight: 400 }}
                                        >
                                            {e.n}
                                        </span>
                                        <span className="block h-px w-8 bg-xv-gold mt-4 transition-all duration-700 group-hover:w-12" />
                                    </div>
                                    <h3
                                        className="font-serif text-[20px] md:text-[22px] text-xv-black leading-tight"
                                        style={{ fontWeight: 400 }}
                                    >
                                        {e.title}
                                    </h3>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* DOMAINES D'INTERVENTION — premium executive grid */}
            <section
                data-testid="operations"
                className="relative bg-[#0a0f1a] text-[#f4ecd8] py-28 md:py-40 overflow-hidden"
            >
                {/* Subtle gold radial accents */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 opacity-[0.05]"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 12% 20%, rgba(212,175,55,0.6) 0px, transparent 38%), radial-gradient(circle at 88% 85%, rgba(212,175,55,0.45) 0px, transparent 44%)",
                    }}
                />
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid lg:grid-cols-12 gap-12 mb-20 md:mb-24">
                        <div className="lg:col-span-5">
                            <Reveal>
                                <div className="flex items-center gap-4 mb-8 text-[#d4af37]">
                                    <span className="block h-px w-12 bg-[#d4af37]/70" />
                                    <span className="overline text-[#d4af37]/90">
                                        {t("operations.kicker")}
                                    </span>
                                </div>
                                <h2
                                    className="font-serif text-[40px] sm:text-[52px] lg:text-[64px] leading-[1.02]"
                                    style={{ fontWeight: 300 }}
                                >
                                    {t("operations.title")}{" "}
                                    <em className="not-italic text-[#d4af37]">
                                        {t("operations.titleEmphasis")}
                                    </em>
                                </h2>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 self-end">
                            <Reveal delay={0.1}>
                                <p className="text-[#f4ecd8]/70 text-[16px] md:text-[17.5px] leading-[1.85] font-light">
                                    {t("operations.intro")}
                                </p>
                            </Reveal>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06] border border-white/[0.06]">
                        {OPERATIONS.map((op, i) => {
                            const Icon = op.icon;
                            const card = opCards[op.key] || {};
                            return (
                                <Reveal key={op.key} delay={(i % 3) * 0.08}>
                                    <article
                                        data-testid={`operation-${i}`}
                                        className="group relative h-full bg-[#0a0f1a] p-10 md:p-12 transition-colors duration-700 hover:bg-[#0d1422]"
                                    >
                                        {/* Top row: numeral + icon */}
                                        <div className="flex items-start justify-between mb-10">
                                            <span
                                                className="font-serif text-[26px] text-[#d4af37] leading-none"
                                                style={{ fontWeight: 400 }}
                                            >
                                                {op.n}
                                            </span>
                                            <Icon
                                                strokeWidth={1}
                                                size={26}
                                                className="text-[#d4af37]/60 transition-colors duration-700 group-hover:text-[#d4af37]"
                                            />
                                        </div>

                                        {/* Title */}
                                        <h3
                                            className="font-serif text-[24px] md:text-[28px] leading-[1.15] text-[#f4ecd8] mb-5"
                                            style={{ fontWeight: 400 }}
                                        >
                                            {card.title}
                                        </h3>

                                        {/* Description */}
                                        <p className="text-[#f4ecd8]/65 text-[14px] leading-[1.85] font-light mb-8 min-h-[5em]">
                                            {card.description}
                                        </p>

                                        {/* Gold divider */}
                                        <span className="block h-px w-10 bg-[#d4af37]/40 mb-6 transition-all duration-700 group-hover:w-16 group-hover:bg-[#d4af37]/80" />

                                        {/* Bullets */}
                                        <ul className="space-y-2.5">
                                            {(card.bullets || []).map((b) => (
                                                <li
                                                    key={b}
                                                    className="flex items-start gap-3 text-[12.5px] text-[#f4ecd8]/75 leading-[1.7]"
                                                >
                                                    <span className="mt-[7px] w-1 h-1 bg-[#d4af37]/70 shrink-0" />
                                                    <span>{b}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </article>
                                </Reveal>
                            );
                        })}
                    </div>

                    {/* Footer line */}
                    <Reveal delay={0.2}>
                        <p className="mt-16 md:mt-20 text-center text-[#f4ecd8]/45 text-[12.5px] tracking-[0.06em] italic font-light max-w-2xl mx-auto leading-[1.85]">
                            {t("operations.footnote")}
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* COLLECTIVITES TEASER */}
            <section
                data-testid="collectivites-teaser"
                className="relative bg-xv-black text-xv-ivory py-28 md:py-40 overflow-hidden"
            >
                <div className="absolute inset-0 opacity-30">
                    <img
                        src={MARBLE}
                        alt=""
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-xv-black/70" />
                </div>
                <div className="relative max-w-[1100px] mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-10 text-xv-goldSoft">
                            <span className="divider-gold" />
                            <span className="overline">
                                Acteurs institutionnels & territoires
                            </span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2
                            className="font-serif text-[40px] md:text-5xl lg:text-[64px] leading-[1.04] max-w-4xl mb-10"
                            style={{ fontWeight: 300 }}
                        >
                            Accompagner les territoires{" "}
                            <em className="not-italic text-xv-goldSoft">
                                dans leurs grandes opérations.
                            </em>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="text-xv-ivory/75 max-w-2xl leading-[1.85] font-light mb-12">
                            Pour les collectivités, opérateurs publics et
                            acteurs du développement économique, le
                            cabinet coordonne un écosystème international
                            d'experts et de partenaires financiers de
                            long terme — calibré au caractère structurant
                            de chaque projet.
                        </p>
                    </Reveal>
                    <Reveal delay={0.3}>
                        <Link
                            to="/collectivites"
                            data-testid="collectivites-cta-from-expertises"
                            className="group inline-flex items-center gap-4 px-8 py-4 border border-xv-ivory/40 text-xv-ivory text-[11px] tracking-[0.3em] uppercase hover:bg-xv-ivory hover:text-xv-black transition-all duration-700"
                        >
                            Découvrir l'approche
                            <ArrowUpRight
                                strokeWidth={1.2}
                                size={16}
                                className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                            />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
